import {
  type HeadersFunction,
  type MetaFunction,
  type LoaderFunctionArgs,
} from "@remix-run/node";
import { format } from "date-fns";
import { typedjson, useTypedLoaderData } from "remix-typedjson";
import ArrowLink from "#app/components/arrowLink.tsx";
import Header from "#app/components/header.tsx";
import Image from "#app/components/image.tsx";
import { CRAFT_QUERY_KEYS, getPageDetails } from "#app/utils/graphql.server.ts";
import { seomaticMeta } from "#app/utils/misc";
import { type NewsEntryPageDetails } from "../../../types/graphql.page.js";

export async function loader({ request }: LoaderFunctionArgs) {
  const url = new URL(request.url);
  const noCache = Object.entries(Object.fromEntries(url.searchParams)).filter(
    ([key]) => CRAFT_QUERY_KEYS.includes(key)
  ).length;

  const data = (await getPageDetails(
    request,
    "news-entry"
  )) as NewsEntryPageDetails;

  return typedjson(
    {
      seo: data?.seo,
      data,
      newsEntry: {
        ...data.newsEntry[0],
        date: format(new Date(data.newsEntry[0]?.date || ""), "MMMM dd, yyyy"),
      },
      subTitle: data.newsEntry?.at(0)?.title || "",
      title: data.entry?.title || "News",
      footer: data.entry?.footer && {
        ...data.entry.footer[0],
        enabled: data.entry.footerEnabled,
      },
    },
    {
      headers: {
        "Cache-Control": noCache
          ? "max-age=0, s-maxage=0"
          : "max-age=60, s-maxage=300",
      },
    }
  );
}

export let headers: HeadersFunction = ({ loaderHeaders }) => {
  return { "Cache-Control": loaderHeaders.get("Cache-Control") || "" };
};

export const meta: MetaFunction<
  typeof loader
> = ({ data }: {data: any}) => {
  return seomaticMeta(data.seo)
}

export default function NewsArticleRoute() {
  const { newsEntry, subTitle, title } = useTypedLoaderData<typeof loader>();

  return (
    <>
      <Header subtitle={subTitle} title={title} />
      <main className="container flex flex-col place-items-start gap-8 py-20">
        {newsEntry.newsListingImage?.at(0)?.url && newsEntry.newsListingImage[0] ? (
          <Image img={newsEntry.newsListingImage[0]} />
        ) : null}
        <div className="font-sans-serif text-xl font-light leading-8 tracking-wide text-green-300">
          {newsEntry.date}
        </div>
        <div
          className=""
          dangerouslySetInnerHTML={{ __html: newsEntry?.newsContent || "" }}
        />
        <ArrowLink text="Back to all news" type={"url"} url="/news" />
      </main>
    </>
  );
}
